import * as THREE from 'three';
import {
  BlendFunction,
  BloomEffect,
  Effect,
  EffectComposer,
  EffectPass,
  GodRaysEffect,
  KernelSize,
  NoiseEffect,
  RenderPass,
  ToneMappingEffect,
  ToneMappingMode,
  VignetteEffect,
} from 'postprocessing';
import type { QualitySettings } from '../core/Quality';

const GRADE_FRAGMENT = /* glsl */ `
uniform float uSaturation;
uniform float uContrast;
uniform vec3 uShadowTint;
uniform vec3 uHighlightTint;
uniform float uLift;
uniform float uFade;

void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
  vec3 c = max(inputColor.rgb, 0.0);
  float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
  c *= mix(vec3(1.0), uShadowTint, 1.0 - smoothstep(0.0, 0.42, l));
  c *= mix(vec3(1.0), uHighlightTint, smoothstep(0.3, 1.0, l));
  c = mix(vec3(l), c, uSaturation);
  vec3 p = pow(c, vec3(1.0 / 2.2));
  p = mix(p, p * p * (3.0 - 2.0 * p), uContrast);
  p = uLift + p * (1.0 - uLift);
  c = pow(max(p, 0.0), vec3(2.2));
  c *= 1.0 - uFade;
  // three.js always creates an alpha canvas and alpha-to-coverage plumes leave partial
  // alpha in the frame; Safari composites that as garbage colours, so output opaque.
  outputColor = vec4(c, 1.0);
}
`;

export class GradeEffect extends Effect {
  constructor() {
    super('GradeEffect', GRADE_FRAGMENT, {
      uniforms: new Map<string, THREE.Uniform>([
        ['uSaturation', new THREE.Uniform(1.02)],
        ['uContrast', new THREE.Uniform(0.22)],
        ['uShadowTint', new THREE.Uniform(new THREE.Vector3(0.94, 0.97, 1.06))],
        ['uHighlightTint', new THREE.Uniform(new THREE.Vector3(1.04, 1.0, 0.93))],
        ['uLift', new THREE.Uniform(0.018)],
        ['uFade', new THREE.Uniform(0)],
      ]),
    });
  }

  set fade(value: number) {
    (this.uniforms.get('uFade') as THREE.Uniform).value = value;
  }
}

export class Pipeline {
  composer: EffectComposer;
  readonly grade = new GradeEffect();
  private bloom: BloomEffect;
  private godRays: GodRaysEffect | null = null;
  private renderPass: RenderPass;

  constructor(
    private readonly renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    private readonly camera: THREE.PerspectiveCamera,
    private readonly sun: THREE.Mesh,
    settings: QualitySettings,
  ) {
    this.composer = new EffectComposer(renderer, {
      frameBufferType: THREE.HalfFloatType,
      multisampling: settings.msaa,
    });
    this.renderPass = new RenderPass(scene, camera);
    this.bloom = new BloomEffect({
      mipmapBlur: true,
      intensity: 0.55,
      luminanceThreshold: 1.1,
      luminanceSmoothing: 0.45,
      radius: 0.72,
      levels: 8,
    });
    this.build(settings);
  }

  private build(settings: QualitySettings): void {
    this.composer.removeAllPasses();
    this.composer.multisampling = Math.min(settings.msaa, this.renderer.capabilities.maxSamples);
    this.composer.addPass(this.renderPass);
    const effects: Effect[] = [];
    this.godRays?.dispose();
    this.godRays = null;
    if (settings.godRays) {
      this.godRays = new GodRaysEffect(this.camera, this.sun, {
        density: 0.93,
        decay: 0.935,
        weight: 0.42,
        exposure: 0.5,
        samples: 48,
        clampMax: 1,
        resolutionScale: 0.5,
        blur: true,
        kernelSize: KernelSize.SMALL,
        blendFunction: BlendFunction.SCREEN,
      });
      effects.push(this.godRays);
    }
    effects.push(this.bloom);
    effects.push(new ToneMappingEffect({ mode: ToneMappingMode.ACES_FILMIC }));
    effects.push(this.grade);
    effects.push(new VignetteEffect({ offset: 0.32, darkness: 0.42 }));
    const grain = new NoiseEffect({ premultiply: true, blendFunction: BlendFunction.SCREEN });
    grain.blendMode.opacity.value = 0.06;
    effects.push(grain);
    this.composer.addPass(new EffectPass(this.camera, ...effects));
  }

  applyQuality(settings: QualitySettings): void {
    this.build(settings);
  }

  setSize(width: number, height: number): void {
    this.composer.setSize(width, height, false);
  }

  render(dt: number): void {
    this.composer.render(dt);
  }

  dispose(): void {
    this.composer.dispose();
  }
}
